import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabase";
import { shouldSkipSupabase } from "../../helpers/guestMode";
import {
  accountPresetKey,
  createFinanceAccount,
  deactivateFinanceAccount,
  FINANCE_ACCOUNT_PRESETS,
  formatFinanceAccountType,
  formatOpeningBalance,
  listFinanceAccounts,
  reactivateFinanceAccount,
  updateFinanceAccount,
} from "../../helpers/financeAccounts";
import { dispatchFinancePositionChanged } from "../../helpers/financeStateEvents";
import { usePermissions } from "../../hooks/usePermissions";
import { CPDropdown } from "../CPDropdown";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { ViewportPortal } from "../shared/ViewportDialog";
import {
  FinanceAccountsDesktop,
  FinanceModalDesktop,
} from "./FinanceDesktop";
import {
  FinanceBackButton,
  FINANCE_CARD_STYLE,
  FINANCE_DANGER_BTN_STYLE,
  FINANCE_HEADER_STYLE,
  FINANCE_INPUT_STYLE,
  FINANCE_LABEL_STYLE,
  FINANCE_MODAL_OVERLAY_STYLE,
  FINANCE_MODAL_PANEL_STYLE,
  FINANCE_PRIMARY_BTN_STYLE,
  FINANCE_SCREEN_STYLE,
  FINANCE_SECONDARY_BTN_STYLE,
  FINANCE_SUBTITLE_STYLE,
  FINANCE_TITLE_STYLE,
  FINANCE_TOOLBAR_STYLE,
} from "./financeUi";

const EMPTY_FORM = {
  name: "",
  institution: "",
  type: "operating",
  currency: "CAD",
  opening_balance: "0",
  active: true,
};

function AccountFormFields({ form, onChange, grid = false }) {
  return (
    <div
      style={
        grid
          ? { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px 16px" }
          : { display: "flex", flexDirection: "column", gap: "12px" }
      }
    >
      <label style={grid ? { gridColumn: "1 / -1" } : undefined}>
        <span style={FINANCE_LABEL_STYLE}>Name</span>
        <input
          style={FINANCE_INPUT_STYLE}
          value={form.name}
          onChange={(event) => onChange({ ...form, name: event.target.value })}
          placeholder="Operating Chequing"
          autoFocus
        />
      </label>

      <label>
        <span style={FINANCE_LABEL_STYLE}>Institution</span>
        <input
          style={FINANCE_INPUT_STYLE}
          value={form.institution}
          onChange={(event) =>
            onChange({ ...form, institution: event.target.value })
          }
          placeholder="Optional"
        />
      </label>

      <CPDropdown
        label="Type"
        value={form.type}
        onChange={(type) => onChange({ ...form, type })}
        includePlaceholder={false}
        options={FINANCE_ACCOUNT_PRESETS.map((preset) => ({
          value: preset.key,
          label: preset.label,
        }))}
      />

      <label>
        <span style={FINANCE_LABEL_STYLE}>Currency</span>
        <input
          style={FINANCE_INPUT_STYLE}
          value={form.currency}
          onChange={(event) =>
            onChange({ ...form, currency: event.target.value })
          }
          placeholder="CAD"
        />
      </label>

      <label>
        <span style={FINANCE_LABEL_STYLE}>Opening Balance</span>
        <input
          style={FINANCE_INPUT_STYLE}
          type="number"
          step="0.01"
          value={form.opening_balance}
          onChange={(event) =>
            onChange({ ...form, opening_balance: event.target.value })
          }
        />
      </label>
    </div>
  );
}

/**
 * Finance accounts CRUD — create / edit / soft-deactivate.
 * No hard delete; inactive accounts remain for history.
 */
export function FinanceAccountsScreen({ currentUser, onBack }) {
  const isDesktop = useIsDesktopViewport();
  const { actor } = usePermissions();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(true);
  const [modalMode, setModalMode] = useState(null); // create | edit
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadAccounts = useCallback(async () => {
    if (shouldSkipSupabase(currentUser)) {
      setAccounts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const rows = await listFinanceAccounts(supabase, {
        includeInactive: true,
      });
      setAccounts(rows);
      setError(null);
    } catch (err) {
      console.error("[Finance] load accounts failed:", err);
      setError("Unable to load accounts.");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  const visibleAccounts = useMemo(() => {
    if (showInactive) {
      return accounts;
    }
    return accounts.filter((account) => account.active);
  }, [accounts, showInactive]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalMode("create");
  };

  const openEdit = (account) => {
    setEditing(account);
    setForm({
      name: account.name || "",
      institution: account.institution || "",
      type: accountPresetKey(account),
      currency: account.currency || "CAD",
      opening_balance: String(account.opening_balance ?? 0),
      active: account.active !== false,
    });
    setModalMode("edit");
  };

  const closeModal = () => {
    if (busy) {
      return;
    }
    setModalMode(null);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        name: form.name,
        institution: form.institution,
        type: form.type,
        currency: form.currency,
        opening_balance: form.opening_balance,
        active: form.active,
      };

      if (modalMode === "create") {
        await createFinanceAccount(supabase, payload, actor || currentUser);
      } else if (editing?.id) {
        await updateFinanceAccount(supabase, editing.id, payload);
      }

      closeModal();
      await loadAccounts();
      dispatchFinancePositionChanged({ reason: "finance_account_saved" });
    } catch (err) {
      console.error("[Finance] save account failed:", err);
      setError(err?.message || "Unable to save account.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivate = async (account) => {
    if (!account?.id) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deactivateFinanceAccount(supabase, account.id);
      await loadAccounts();
      dispatchFinancePositionChanged({ reason: "finance_account_deactivated" });
    } catch (err) {
      console.error("[Finance] deactivate account failed:", err);
      setError(err?.message || "Unable to deactivate account.");
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async (account) => {
    if (!account?.id) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await reactivateFinanceAccount(supabase, account.id);
      await loadAccounts();
      dispatchFinancePositionChanged({ reason: "finance_account_reactivated" });
    } catch (err) {
      console.error("[Finance] reactivate account failed:", err);
      setError(err?.message || "Unable to reactivate account.");
    } finally {
      setBusy(false);
    }
  };

  if (isDesktop) {
    return (
      <FinanceAccountsDesktop
        accounts={accounts}
        loading={loading}
        error={error}
        busy={busy}
        showInactive={showInactive}
        onShowInactive={setShowInactive}
        onBack={onBack}
        onCreate={openCreate}
        onEdit={openEdit}
        onDeactivate={handleDeactivate}
        onReactivate={handleReactivate}
      >
        {modalMode ? (
          <FinanceModalDesktop
            title={modalMode === "create" ? "New Account" : "Edit Account"}
            onClose={closeModal}
            onSave={handleSave}
            busy={busy}
          >
            <AccountFormFields form={form} onChange={setForm} grid />
          </FinanceModalDesktop>
        ) : null}
      </FinanceAccountsDesktop>
    );
  }

  return (
    <div style={FINANCE_SCREEN_STYLE}>
      <FinanceBackButton onClick={onBack} />

      <div style={FINANCE_HEADER_STYLE}>
        <h1 style={FINANCE_TITLE_STYLE}>Accounts</h1>
        <p style={FINANCE_SUBTITLE_STYLE}>
          Create and manage financial accounts. Inactive accounts are retained
          for history.
        </p>
      </div>

      <div style={FINANCE_TOOLBAR_STYLE}>
        <button
          type="button"
          style={FINANCE_PRIMARY_BTN_STYLE}
          onClick={openCreate}
          disabled={busy}
        >
          New Account
        </button>
        <button
          type="button"
          style={FINANCE_SECONDARY_BTN_STYLE}
          onClick={() => setShowInactive((value) => !value)}
        >
          {showInactive ? "Hide Inactive" : "Show Inactive"}
        </button>
      </div>

      {error ? (
        <p style={{ color: "#f87171", marginBottom: "12px" }}>{error}</p>
      ) : null}

      {loading ? (
        <p style={{ opacity: 0.6 }}>Loading accounts…</p>
      ) : visibleAccounts.length === 0 ? (
        <div style={FINANCE_CARD_STYLE}>
          <p style={{ margin: 0, opacity: 0.7 }}>
            No accounts yet. Create your first operating account to begin.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {visibleAccounts.map((account) => (
            <div
              key={account.id}
              style={{
                ...FINANCE_CARD_STYLE,
                opacity: account.active ? 1 : 0.62,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <div style={{ fontSize: "17px", fontWeight: 700 }}>
                    {account.name}
                  </div>
                  <div style={{ opacity: 0.7, marginTop: "4px", fontSize: "13px" }}>
                    {formatFinanceAccountType(account)}
                    {account.institution ? ` · ${account.institution}` : ""}
                  </div>
                  <div style={{ opacity: 0.7, marginTop: "4px", fontSize: "13px" }}>
                    Opening:{" "}
                    {formatOpeningBalance(
                      account.opening_balance,
                      account.currency,
                    )}
                  </div>
                  {!account.active ? (
                    <div
                      style={{
                        marginTop: "8px",
                        fontSize: "12px",
                        color: "rgba(248,113,113,0.9)",
                        fontWeight: 700,
                      }}
                    >
                      Inactive
                    </div>
                  ) : null}
                </div>

                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    style={FINANCE_SECONDARY_BTN_STYLE}
                    onClick={() => openEdit(account)}
                    disabled={busy}
                  >
                    Edit
                  </button>
                  {account.active ? (
                    <button
                      type="button"
                      style={FINANCE_DANGER_BTN_STYLE}
                      onClick={() => handleDeactivate(account)}
                      disabled={busy}
                    >
                      Deactivate
                    </button>
                  ) : (
                    <button
                      type="button"
                      style={FINANCE_PRIMARY_BTN_STYLE}
                      onClick={() => handleReactivate(account)}
                      disabled={busy}
                    >
                      Reactivate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modalMode ? (
        <ViewportPortal>
        <div style={FINANCE_MODAL_OVERLAY_STYLE} onClick={closeModal}>
          <div
            style={FINANCE_MODAL_PANEL_STYLE}
            onClick={(event) => event.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, marginBottom: "14px", fontSize: "20px" }}>
              {modalMode === "create" ? "New Account" : "Edit Account"}
            </h2>
            <AccountFormFields form={form} onChange={setForm} />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "18px",
              }}
            >
              <button
                type="button"
                style={FINANCE_SECONDARY_BTN_STYLE}
                onClick={closeModal}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                style={FINANCE_PRIMARY_BTN_STYLE}
                onClick={handleSave}
                disabled={busy}
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
        </ViewportPortal>
      ) : null}
    </div>
  );
}
